const API_BASE = (import.meta.env.VITE_ACCOUNT_PORTAL_API_URL || '').replace(/\/$/, '');
const ACCESS_KEY = 'accountPortal_accessToken';
const REFRESH_KEY = 'accountPortal_refreshToken';

function requireBase() {
  if (!API_BASE) {
    throw new Error('VITE_ACCOUNT_PORTAL_API_URL is not configured');
  }
}

export function getStoredAccessToken() {
  return sessionStorage.getItem(ACCESS_KEY) || '';
}

export function setStoredAccessToken(token) {
  if (token) sessionStorage.setItem(ACCESS_KEY, token);
  else sessionStorage.removeItem(ACCESS_KEY);
}

export function getStoredRefreshToken() {
  return sessionStorage.getItem(REFRESH_KEY) || '';
}

export function setStoredRefreshToken(token) {
  if (token) sessionStorage.setItem(REFRESH_KEY, token);
  else sessionStorage.removeItem(REFRESH_KEY);
}

export function clearStoredSession() {
  setStoredAccessToken('');
  setStoredRefreshToken('');
}

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail;
    const message = typeof detail === 'string' ? detail : data?.message || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data;
}

async function apiFetch(path, { method = 'GET', body, auth = true } = {}) {
  requireBase();
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getStoredAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body != null ? JSON.stringify(body) : undefined,
  });
  return parseJson(res);
}

export async function requestMagicLink(email) {
  return apiFetch('/accountPortal/auth/magic-link/request', {
    method: 'POST',
    body: { email },
    auth: false,
  });
}

export async function verifyMagicLink(token) {
  const data = await apiFetch('/accountPortal/auth/magic-link/verify', {
    method: 'POST',
    body: { token },
    auth: false,
  });
  if (data.accessToken) setStoredAccessToken(data.accessToken);
  if (data.refreshToken) setStoredRefreshToken(data.refreshToken);
  return data;
}

export async function refreshSession() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return { ok: true, authenticated: false };
  }
  const data = await apiFetch('/accountPortal/auth/refresh', {
    method: 'POST',
    body: { refreshToken },
    auth: false,
  });
  if (data.authenticated === false) {
    clearStoredSession();
    return data;
  }
  if (data.accessToken) setStoredAccessToken(data.accessToken);
  if (data.refreshToken) setStoredRefreshToken(data.refreshToken);
  return data;
}

export async function logout() {
  try {
    await apiFetch('/accountPortal/auth/logout', { method: 'POST', auth: false });
  } finally {
    clearStoredSession();
  }
}

export async function getMe() {
  return apiFetch('/accountPortal/me');
}

export async function getCompany(companyId) {
  return apiFetch(`/accountPortal/company/${companyId}`);
}

export async function listMembers(companyId) {
  const data = await apiFetch(`/accountPortal/company/${companyId}/members`);
  return data.items || [];
}

export async function inviteMember({ companyId, email, role }) {
  return apiFetch('/accountPortal/company/invite', {
    method: 'POST',
    body: { companyId, email, role },
  });
}

export async function removeMember({ companyId, memberId }) {
  return apiFetch('/accountPortal/company/remove', {
    method: 'POST',
    body: { companyId, memberId },
  });
}

export async function changeRole({ companyId, memberId, role }) {
  return apiFetch('/accountPortal/company/role', {
    method: 'POST',
    body: { companyId, memberId, role },
  });
}

export async function claimProductLicense({ companyId }) {
  return apiFetch('/accountPortal/company/claim-product-license', {
    method: 'POST',
    body: { companyId },
  });
}

export async function assignSeat({ companyId, memberId }) {
  return apiFetch('/accountPortal/company/assign-seat', {
    method: 'POST',
    body: { companyId, memberId },
  });
}

export async function removeSeat({ companyId, memberId }) {
  return apiFetch('/accountPortal/company/remove-seat', {
    method: 'POST',
    body: { companyId, memberId },
  });
}

export async function getCompanyUsage(companyId, days = 30) {
  return apiFetch(`/accountPortal/company/${companyId}/usage?days=${days}`);
}
